import urlJoin from 'url-join';

type RequestOptions = RequestInit & {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
};

export const HTTP_NOT_FOUND = 404;

export class FetchError extends Error {
  constructor(
    message: string,
    public readonly statusCode: number,
    public readonly code?: string
  ) {
    super(message);
  }
}

// Client-side cache for runtime config
let clientConfigCache: Record<string, string> | null = null;

const getClientConfig = (): Record<string, string> => {
  if (typeof window === 'undefined') {
    throw new Error('getClientConfig can only be called on the client side');
  }

  if (!clientConfigCache) {
    throw new Error(
      'Runtime config not initialized. Make sure RuntimeConfigProvider is mounted.'
    );
  }

  return clientConfigCache;
};

// Initialize the client config cache (called by the context provider)
export const initializeClientConfig = (config: Record<string, string>) => {
  if (typeof window !== 'undefined') {
    clientConfigCache = config;
  }
};

export const withBFFUrl = (suffix: string): string => {
  if (typeof window === 'undefined') {
    return urlJoin(process.env.PUBLIC_APP_URL || '', suffix);
  }
  const config = getClientConfig();
  return urlJoin(window.location.origin + (config.BASE_PATH || ''), suffix);
};

export const withApiBaseUrl = (suffix: string): string => {
  if (typeof window === 'undefined') {
    return urlJoin(process.env.PUBLIC_API_URL || '', suffix);
  }
  const config = getClientConfig();
  return urlJoin(config.PUBLIC_API_URL || '', suffix);
};

export const withDocumentsApiBaseUrl = (suffix: string): string => {
  if (typeof window === 'undefined') {
    return urlJoin(process.env.PUBLIC_DOCUMENTS_API_URL || '', suffix);
  }
  const config = getClientConfig();
  return urlJoin(config.PUBLIC_DOCUMENTS_API_URL || '', suffix);
};

export const withArrivalsApiBaseUrl = (suffix: string): string => {
  if (typeof window === 'undefined') {
    return urlJoin(process.env.PUBLIC_ARRIVALS_API_URL || '', suffix);
  }
  const config = getClientConfig();
  return urlJoin(config.PUBLIC_ARRIVALS_API_URL || '', suffix);
};

export const withPaymentApiBaseUrl = (suffix: string): string => {
  if (typeof window === 'undefined') {
    return urlJoin(process.env.PUBLIC_PAYMENT_API_URL || '', suffix);
  }
  const config = getClientConfig();
  return urlJoin(config.PUBLIC_PAYMENT_API_URL || '', suffix);
};

export const withLettingsApiBaseUrl = (suffix: string): string => {
  if (typeof window === 'undefined') {
    return urlJoin(process.env.PUBLIC_LETTINGS_API_URL || '', suffix);
  }
  const config = getClientConfig();
  return urlJoin(config.PUBLIC_LETTINGS_API_URL || '', suffix);
};

export const withPlayPassApiBaseUrl = (suffix: string): string => {
  if (typeof window === 'undefined') {
    return urlJoin(process.env.PUBLIC_PLAY_PASS_API_URL || '', suffix);
  }
  const config = getClientConfig();
  return urlJoin(config.PUBLIC_PLAY_PASS_API_URL || '', suffix);
};

export const withAccountApiBaseUrl = (suffix: string): string => {
  if (typeof window === 'undefined') {
    return urlJoin(process.env.PUBLIC_ACCOUNT_API_URL || '', suffix);
  }
  const config = getClientConfig();
  return urlJoin(config.PUBLIC_ACCOUNT_API_URL || '', suffix);
};

export const withEagleEyeApiBaseUrl = (suffix: string): string => {
  if (typeof window === 'undefined') {
    return urlJoin(process.env.PUBLIC_EAGLE_EYE_API_URL || '', suffix);
  }
  const config = getClientConfig();
  return urlJoin(config.PUBLIC_EAGLE_EYE_API_URL || '', suffix);
};

export const withAuthenticationUrl = (suffix: string): string => {
  if (typeof window === 'undefined') {
    return urlJoin(process.env.PUBLIC_AUTHENTICATION_API_URL || '', suffix);
  }
  const config = getClientConfig();
  return urlJoin(config.PUBLIC_AUTHENTICATION_API_URL || '', suffix);
};

export const fetchWrapper = async <T = any>(
  url: string,
  options: RequestOptions = {},
  unauthRedirect = true
): Promise<T> => {
  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (headers['Content-Type'] === 'multipart/form-data') {
    // @ts-ignore
    delete headers['Content-Type'];
  }

  const res = await fetch(url, {
    ...options,
    credentials: options.credentials
      ? options.credentials
      : !options.method || options.method.toLowerCase() === 'get'
      ? 'omit'
      : 'include',
    headers,
  });

  if (res?.ok) {
    if (res.status === 204) return {} as T;
    let payload = null;
    switch (headers.Accept) {
      case 'application/pdf': {
        payload = await res.blob();
        break;
      }
      default: {
        payload = await res.json();
      }
    }
    return payload;
  } else {
    if (res?.status === HTTP_NOT_FOUND) {
      throw new FetchError(res.statusText, res.status);
    }
    const error = await res?.json();
    const status = error?.statusCode || res?.status || 500;
    if (unauthRedirect && typeof window !== 'undefined' && status === 401) {
      const config = getClientConfig();
      window.location.assign(
        `${config.PUBLIC_LOGIN_URL}?sessionExpired=true&redirect=${window.location.href}`
      );
    }
    throw new FetchError(error?.message, status, error?.code);
  }
};

export const swrFetcher = (url: string) =>
  fetchWrapper(withApiBaseUrl(url), { method: 'GET', credentials: 'include' });

export const swrLettingsFetcher = <T>(url: string): Promise<T> =>
  fetchWrapper<T>(withLettingsApiBaseUrl(url), {
    method: 'GET',
    credentials: 'include',
  });

export const swrLocalFetcher = (url: string) =>
  fetchWrapper(withBFFUrl(url), { method: 'GET', credentials: 'include' });
